import React, { useCallback, useState } from 'react';
import type { ColDef, EditableCallbackParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import {
  getRatesHistory,
  saveRatesHistoryBatch,
} from '../../api/ratesHistory';
import type { RateHistory } from '../../types/RateHistory';
import { t } from '../../i18n/t';

// "שערים היסטוריים" — composite-PK (assetId, dDate). First historical table
// in LOP. Both PK columns editable on Add only.
//
// Date-range filter (default: 2 weeks back -> today). Server-side filtered;
// the page tracks DRAFT date state (what's in the inputs) separately from
// ACTIVE state (the values currently driving getRows / saveBatch). Only the
// "Fetch" button copies draft -> active and triggers a re-fetch via the
// useCallback identity change.
const columnDefs: ColDef<RateHistory>[] = [
  {
    field: 'dDate',
    headerName: t('ratesHistory.columns.dDate'),
    width: 140,
    editable: (params: EditableCallbackParams<RateHistory>) =>
      (params.data as { __status?: string } | undefined)?.__status === 'added',
    sort: 'desc',
  },
  {
    field: 'assetId',
    headerName: t('ratesHistory.columns.assetId'),
    width: 130,
    editable: (params: EditableCallbackParams<RateHistory>) =>
      (params.data as { __status?: string } | undefined)?.__status === 'added',
    cellDataType: 'number',
  },
  {
    field: 'baseRate',
    headerName: t('ratesHistory.columns.baseRate'),
    width: 140,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'currRate',
    headerName: t('ratesHistory.columns.currRate'),
    width: 140,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'nav',
    headerName: t('ratesHistory.columns.nav'),
    width: 140,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'value1Point',
    headerName: t('ratesHistory.columns.value1Point'),
    width: 140,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'sourceId',
    headerName: t('ratesHistory.columns.sourceId'),
    width: 110,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'navDate',
    headerName: t('ratesHistory.columns.navDate'),
    width: 160,
    editable: true,
  },
  // V1.71.5 — Black-Scholes inputs (mirror Rates).
  {
    field: 'ivBaseAsset',
    headerName: t('ratesHistory.columns.ivBaseAsset'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'ribitOptions',
    headerName: t('ratesHistory.columns.ribitOptions'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'kofelBaseRate',
    headerName: t('ratesHistory.columns.kofelBaseRate'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
];

const newRateHistoryDefaults = (): Partial<RateHistory> => ({
  assetId: 0,
  dDate: isoToday(),
  baseRate: null,
  sourceId: null,
  nav: null,
  value1Point: null,
  navDate: null,
  currRate: null,
  ivBaseAsset: null,
  ribitOptions: null,
  kofelBaseRate: null,
});

export function RatesHistoryPage() {
  // DRAFT — what's currently in the date inputs (free for user to edit).
  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  // ACTIVE — the dates that drive the current fetch. Initial mount fetches
  // with the default range; subsequent fetches happen only when the user
  // hits "Fetch", which copies draft -> active.
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);

  const getRows = useCallback(
    () => getRatesHistory(activeFrom, activeTo),
    [activeFrom, activeTo],
  );
  const saveBatch = useCallback(
    (
      added: RateHistory[],
      modified: RateHistory[],
      deletedIdKeys: unknown[],
    ) =>
      saveRatesHistoryBatch(
        added,
        modified,
        deletedIdKeys as string[],
        activeFrom,
        activeTo,
      ),
    [activeFrom, activeTo],
  );

  function handleApply() {
    setActiveFrom(draftFrom);
    setActiveTo(draftTo);
    setFetched(true);
  }

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={handleApply}
      />
      <LopCrudPage<RateHistory>
        awaitingFetch={!fetched}
        resourceCode="ratesHistory"
        titleKey="ratesHistory.pageTitle"
        idField="idKey"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newRateHistoryDefaults}
      />
    </div>
  );
}
